import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { WifiIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface NetworkState {
  isOnline: boolean;
  wasOffline: boolean;
  downtime: number;
  lastOfflineAt: Date | null;
  reconnectAttempts: number;
}

interface NetworkMonitorProps {
  onOnline?: () => void;
  onOffline?: () => void;
  showToasts?: boolean;
  autoRetryFailedRequests?: boolean;
}

// Custom hook for network monitoring
export function useNetworkStatus() {
  const [networkState, setNetworkState] = useState<NetworkState>({
    isOnline: navigator.onLine,
    wasOffline: false,
    downtime: 0,
    lastOfflineAt: null,
    reconnectAttempts: 0
  });

  const downtimeInterval = useRef<NodeJS.Timeout>();
  const queryClient = useQueryClient();

  const handleOnline = useCallback(() => {
    const now = new Date();
    const downtime = networkState.lastOfflineAt 
      ? now.getTime() - networkState.lastOfflineAt.getTime()
      : 0;

    setNetworkState(prev => ({
      ...prev,
      isOnline: true,
      wasOffline: prev.wasOffline || !prev.isOnline,
      downtime,
      reconnectAttempts: 0
    }));

    // Clear downtime tracking
    if (downtimeInterval.current) {
      clearInterval(downtimeInterval.current);
      downtimeInterval.current = undefined;
    }

    // Invalidate queries to refetch data
    queryClient.invalidateQueries();
    
    // Retry failed queries
    queryClient.getQueryCache().getAll().forEach(query => {
      if (query.state.status === 'error') {
        queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
    });
  }, [networkState.lastOfflineAt, queryClient]);

  const handleOffline = useCallback(() => {
    const now = new Date();
    setNetworkState(prev => ({
      ...prev,
      isOnline: false,
      lastOfflineAt: now,
      downtime: 0
    }));

    // Start tracking downtime
    downtimeInterval.current = setInterval(() => {
      setNetworkState(prev => ({
        ...prev,
        downtime: now ? Date.now() - now.getTime() : prev.downtime
      }));
    }, 1000);
  }, []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (downtimeInterval.current) {
        clearInterval(downtimeInterval.current);
      }
    };
  }, [handleOnline, handleOffline]);

  return networkState;
}

// Network status banner component
export function NetworkStatusBanner() {
  const networkState = useNetworkStatus();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!networkState.isOnline && !dismissed) {
      setShowBanner(true);
    } else if (networkState.isOnline && networkState.wasOffline) {
      setShowBanner(false);
      setDismissed(false);
      
      // Show reconnection toast
      toast.success('Connection restored', {
        description: `You were offline for ${Math.round(networkState.downtime / 1000)} seconds`,
        duration: 3000
      });
    }
  }, [networkState.isOnline, networkState.wasOffline, networkState.downtime, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-50 border-b border-yellow-200">
      <Alert className="rounded-none border-0 bg-transparent">
        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <span className="text-yellow-800 font-medium">
              You're currently offline
            </span>
            <span className="text-yellow-600 text-sm">
              Some features may not work properly
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleRetry}
              className="border-yellow-300 text-yellow-700 hover:bg-yellow-100"
            >
              <WifiIcon className="h-4 w-4 mr-1" />
              Retry
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="text-yellow-700 hover:bg-yellow-100"
            >
              Dismiss
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

// Main network monitor component
export function NetworkMonitor({ 
  onOnline, 
  onOffline, 
  showToasts = true,
  autoRetryFailedRequests = true 
}: NetworkMonitorProps) {
  const networkState = useNetworkStatus();
  const queryClient = useQueryClient();
  
  // Track previous online state to detect transitions
  const prevOnlineRef = useRef(networkState.isOnline);

  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    const isOnline = networkState.isOnline;

    // Handle online transition
    if (!wasOnline && isOnline) {
      onOnline?.();
      
      if (showToasts && networkState.wasOffline) {
        toast.success('Back online!', {
          description: 'Connection restored. Syncing data...',
          duration: 3000
        });
      }

      // Auto-retry failed requests
      if (autoRetryFailedRequests) {
        const failedQueries = queryClient.getQueryCache().getAll().filter(
          query => query.state.status === 'error'
        );

        if (failedQueries.length > 0) {
          failedQueries.forEach(query => {
            queryClient.invalidateQueries({ queryKey: query.queryKey });
          });

          toast.info(`Retrying ${failedQueries.length} failed requests...`, {
            duration: 2000
          });
        }
      }
    }

    // Handle offline transition
    if (wasOnline && !isOnline) {
      onOffline?.();
      
      if (showToasts) {
        toast.warning('Connection lost', {
          description: 'You\'re now offline. Some features may be limited.',
          duration: 4000
        });
      }
    }

    prevOnlineRef.current = isOnline;
  }, [
    networkState.isOnline, 
    networkState.wasOffline, 
    onOnline, 
    onOffline, 
    showToasts, 
    autoRetryFailedRequests,
    queryClient
  ]);

  // Set up React Query's online manager
  useEffect(() => {
    // React Query will automatically pause queries when offline
    // and resume when online based on navigator.onLine
    queryClient.getQueryCache().getAll().forEach(query => {
      if (!networkState.isOnline && query.state.status === 'loading') {
        // Optionally cancel in-flight requests when going offline
        query.cancel?.();
      }
    });
  }, [networkState.isOnline, queryClient]);

  return <NetworkStatusBanner />;
}

// Higher-order component to wrap apps with network monitoring
export function withNetworkMonitoring<P extends object>(
  Component: React.ComponentType<P>,
  networkMonitorProps?: NetworkMonitorProps
) {
  const WrappedComponent = (props: P) => (
    <>
      <NetworkMonitor {...networkMonitorProps} />
      <Component {...props} />
    </>
  );

  WrappedComponent.displayName = `withNetworkMonitoring(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

// Hook to check if device has network connectivity with optional ping test
export function useConnectivityCheck() {
  const [isConnected, setIsConnected] = useState(navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);

  const checkConnectivity = useCallback(async (pingUrl: string = '/api/health') => {
    setIsChecking(true);
    
    try {
      // Try to fetch a small endpoint to verify actual connectivity
      const response = await fetch(pingUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      
      const connected = response.ok;
      setIsConnected(connected);
      return connected;
    } catch (error) {
      setIsConnected(false);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsConnected(true);
      // Optionally verify with ping test
      checkConnectivity();
    };

    const handleOffline = () => {
      setIsConnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkConnectivity]);

  return {
    isConnected,
    isChecking,
    checkConnectivity
  };
}